import React, { forwardRef, useCallback } from "react";
import { NavLink } from "react-router";
import { Icon } from "@/shared/ui/Icon";
import { Touchable } from "@/shared/ui/Touchable";
import {
  SidebarLink as ISidebarLink,
  useNavigationContainer,
} from "@/shared/contexts/navigation.context";
import { cn } from "@/shared/utils/shadcn";

export interface SidebarLinkProps extends ISidebarLink {
  isExpanded?: boolean;
  onClick?: () => void;
  variant?: "default" | "blue";
  id?: string;
}

const SidebarLink = forwardRef<HTMLDivElement, SidebarLinkProps>(
  (props, ref) => {
    const {
      label,
      url,
      onClick,
      icon,
      isExpanded,
      isHidden,
      id,
      isActive: defaultIsActive,
      variant = "default",
    } = props;

    const { shouldUseMobileUI, closeMobileSidebar } = useNavigationContainer();

    if (isHidden) return null;

    const activeClasses = "bg-grey-100 ";

    const ContentComponent = useCallback(
      ({ isActive }: { isActive: boolean }) => (
        <div
          className={`relative flex flex-row items-center h-8 rounded-md border border-transparent hover:bg-grey-100  ${
            defaultIsActive || isActive ? activeClasses : ""
          }`}
        >
          {(defaultIsActive || isActive) && (
            <div className="absolute h-4 w-[2px] bg-blue-500 left-0 rounded-sm" />
          )}
          <div className="flex items-center">
            <Icon
              icon={icon}
              color={variant === "blue" ? "blue" : "darkGrey"}
              size="sm"
              className="ml-4"
            />
          </div>
          <span
            className={cn(
              "ml-3 text-nowrap text-sm text-ellipsis, overflow-hidden",
              isExpanded ? "max-w-xs" : "max-w-0",
              variant === "blue" && "text-blue-500"
            )}
          >
            {label}
          </span>
        </div>
      ),
      [isExpanded, defaultIsActive]
    );

    const closeSidebarIfMobile = useCallback(() => {
      if (shouldUseMobileUI) {
        closeMobileSidebar();
      }
    }, [shouldUseMobileUI, closeMobileSidebar]);

    const handleClick = useCallback(() => {
      onClick?.();
      closeSidebarIfMobile();
    }, [onClick, closeSidebarIfMobile]);

    if (!!onClick || !url) {
      return (
        <Touchable
          ref={ref}
          onClick={handleClick}
          id={id}
          className="shadow-none hover:shadow-none w-full"
        >
          <ContentComponent isActive={defaultIsActive || false} />
        </Touchable>
      );
    }

    return (
      <NavLink
        to={url || "/"}
        className="border-none"
        id={id}
        onClick={closeSidebarIfMobile}
      >
        {({ isActive }) => <ContentComponent isActive={isActive} />}
      </NavLink>
    );
  }
);

export { SidebarLink };
